import { DownOutlined, FolderOpenOutlined, ImportOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, message, Result, Segmented, Tabs, Tooltip } from 'antd';
import axios from 'axios';
import { Selection } from 'd3';
import { saveAs } from 'file-saver';
import { isEqual } from 'lodash';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import germanFlag from 'url:../assets/images/languages/de.png';
import englishFlag from 'url:../assets/images/languages/us.png';
import {
  analysisFileName,
  currentPalette,
  parseAnalysis,
  readAnalysis,
  SavedAnalysis,
  withCurrentPalette,
  writeAnalysis
} from './analysisStorage';
import { apiErrorMessage } from './apiError';
import { midiFromName, play, PlayableNote, Playback, stopPlayback } from './audio/player';
import DiscoveryPanel, { discoveryPanelID } from './DiscoveryPanel';
import ErrorBoundary from './ErrorBoundary';
import FamilyDetail from './FamilyDetail';
import { getAPIURL } from './host';
import { useDiscovery, useFamily } from './hooks/useDiscovery';
import useMusicSheet from './hooks/useMusicSheet';
import useMusicSheets from './hooks/useMusicSheets';
import { occursFirstInVoice } from './hooks/usePatterns';
import useVoices from './hooks/useVoices';
import ImportSheet from './ImportSheet';
import './layout.css';
import MelodyAnalysis, { BeamStemElementSelection } from './MelodyAnalysis';
import MelodyGraph from './MelodyGraph';
import {
  Family,
  FamilyOccurrence,
  automaticFamilyName,
  familyColors,
  familyConnections,
  familyLabels,
  familyName,
  familyPatternID,
  familyToPattern,
  ownPatternsByFamily
} from './models/Discovery';
import { NoteContext } from './models/Note';
import {
  Occurrence,
  occurrenceKey,
  Operator,
  Pattern,
  PatternLabelGenerator
} from './models/Pattern';
import MusicSheetSelection from './MusicSheetSelection';
import OnBoarding from './OnBoarding';
import ScorePlayer from './ScorePlayer';
import { followPlayback, noteSpansOf } from './scoreFollowing';
import SheetZoom, { PageModeSwitch, storedPageMode } from './SheetZoom';
import Suggestions from './Suggestions';
import { isLegacyColor } from './palette';
import { ThemeContext, voiceColors } from './ThemeContext';
import Timeline from './Timeline';

export type NoteElementSelection = Selection<SVGGElement, NoteContext, null, undefined>;

type AnalysisTab = 'discovery' | 'operators' | 'transitions';

const { Content, Header } = Layout;

const AlignedSegmented = styled(Segmented)`
  .ant-segmented-item-label {
    align-items: center;
    display: flex;
  }
`;

const FullHeightLayout = styled(Layout)`
  height: 100%;
`;

export const mainWrapperID = 'main-wrapper';

const App = () => {
  const [absoluteOffsetPageMap, setAbsoluteOffsetPageMap] = useState<Map<number, number>>(
    new Map()
  );
  const [analysisTab, setAnalysisTab] = useState<AnalysisTab>('discovery');
  const [configuringID, setConfiguringID] = useState<string>(undefined);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hoveredOccurrenceIndex, setHoveredOccurrenceIndex] = useState<number>(undefined);
  const [hoveredOccurrences, setHoveredOccurrences] = useState<Occurrence[]>([]);
  const [hoveredPatternIDs, setHoveredPatternIDs] = useState<string[]>([]);
  const [isImportOpen, setIsImportOpen] = useState<boolean>(false);
  const [isMusicSheetRendered, setIsMusicSheetRendered] = useState<boolean>(false);
  const [newPattern, setNewPattern] = useState<Pattern | undefined>(undefined);
  const [noteBeamElements, setNoteBeamElements] = useState<BeamStemElementSelection>();
  const [noteElements, setNoteElements] = useState<NoteElementSelection>();
  const [noteStemElements, setNoteStemElements] = useState<BeamStemElementSelection>();
  const [numberOfPages, setNumberOfPages] = useState<number>();
  const [patterns, setPatterns] = useState<Pattern[]>([]);
  const [playing, setPlaying] = useState<{ label: string; playback: Playback }>();
  const [selectedFamilyLabel, setSelectedFamilyLabel] = useState<string>();
  const [selectedMusicSheetID, setSelectedMusicSheetID] = useState<string>();
  const {
    error: musicSheetError,
    musicSheet: selectedMusicSheet,
    retry: retryMusicSheet
  } = useMusicSheet(selectedMusicSheetID);
  const [showOnBoarding, setShowOnBoarding] = useState<boolean>(false);
  const [suggestingID, setSuggestionsID] = useState<string>(undefined);
  const [theme, setTheme] = useContext(ThemeContext);
  const [sheetZoom, setSheetZoom] = useState(1);
  // Names the analyst gave discovered families (by discovery label); the others keep their starting names.
  const [familyNames, setFamilyNames] = useState<Record<string, string>>({});
  // Per discovered family (by discovery label), the operators whose entries are not highlighted (Suggestions panel).
  const [hiddenFamilyOperators, setHiddenFamilyOperators] = useState<Record<string, Operator[]>>(
    {}
  );
  const [pageMode, setPageMode] = useState(storedPageMode);
  const [visibleOperatorsByDefault, setVisibleOperatorsByDefault] = useState<Operator[]>([
    Operator.TRANSPOSITION
  ]);

  const { i18n, t: translate } = useTranslation();
  const [musicSheets, reloadMusicSheets] = useMusicSheets();
  const voices = useVoices(selectedMusicSheetID);
  const discovery = useDiscovery(selectedMusicSheetID);

  const families = discovery.data?.families ?? [];
  const labels = useMemo(() => familyLabels(families), [discovery.data]);
  const colors = useMemo(() => familyColors(families), [discovery.data]);
  const ownPatterns = useMemo(
    () => ownPatternsByFamily(families, patterns),
    [discovery.data, patterns]
  );
  const selectedFamily = families.find((family) => family.label === selectedFamilyLabel);
  const visibleFamilyLabels = families
    .filter((family) => patterns.some((pattern) => pattern.id === familyPatternID(family)))
    .map((family) => family.label);

  // The pattern the analyst configures, if it is one of their own: its family is shown under "Transitions".
  const configuringPattern = patterns.find((pattern) => pattern.id === configuringID);
  const ownPattern =
    configuringPattern && !configuringPattern.id.startsWith('family-')
      ? configuringPattern
      : undefined;
  const ownFamily = useFamily(selectedMusicSheetID, ownPattern?.occurrences[0]);
  // A found pattern clicked in the sheet or the timeline: its family from the discovery (no extra request).
  const configuringFamily = families.find((family) => familyPatternID(family) === configuringID);
  // The family the Suggestions panel shows the operators of: one clicked in the sheet, the timeline or the graph, or
  // the one selected among the discovered patterns, unless the analyst configures a pattern of their own.
  const suggestionsFamily =
    configuringFamily ?? (configuringPattern === undefined ? selectedFamily : undefined);

  useEffect(() => {
    if (configuringFamily) setSelectedFamilyLabel(configuringFamily.label);
  }, [configuringID]);

  useEffect(() => {
    setSelectedFamilyLabel(undefined);
    setFamilyNames({});
    setHiddenFamilyOperators({});
    stopPlayback();
  }, [selectedMusicSheetID]);

  // Autosave per piece (audit ISSUE-003). The notes of a piece are mapped once per rendering, after its patterns were
  // reset: that is the moment to restore. Saving waits for the restore, so an empty list never overwrites saved work.
  const [restoredSheetID, setRestoredSheetID] = useState<string>();
  const analysisFileInput = useRef<HTMLInputElement>(null);

  const applyAnalysis = (saved: SavedAnalysis) => {
    const analysis = withCurrentPalette(saved);
    analysis.patterns.forEach((pattern) => PatternLabelGenerator.take(pattern.label));
    setPatterns(analysis.patterns.map((pattern) => ({ ...pattern })));
    if (analysis.defaultOperators?.length) setVisibleOperatorsByDefault(analysis.defaultOperators);
    setFamilyNames(analysis.names ?? {});
    setConfiguringID(undefined);
    setNewPattern(undefined);
    setSuggestionsID(undefined);
  };

  useEffect(() => {
    if (!noteElements || !selectedMusicSheet) return;
    const saved = readAnalysis(selectedMusicSheet);
    if (saved) applyAnalysis(saved);
    setRestoredSheetID(selectedMusicSheet.id);
  }, [noteElements]);

  const currentAnalysis = (): SavedAnalysis | undefined =>
    selectedMusicSheet && {
      app: 'MelodyVis',
      defaultOperators: visibleOperatorsByDefault,
      names: familyNames,
      palette: currentPalette,
      patterns,
      savedAt: new Date().toISOString(),
      sheet: { composer: selectedMusicSheet.composer, title: selectedMusicSheet.title },
      version: 1
    };

  useEffect(() => {
    if (!selectedMusicSheet || restoredSheetID !== selectedMusicSheet.id) return;
    writeAnalysis(currentAnalysis());
  }, [patterns, visibleOperatorsByDefault, familyNames, restoredSheetID]);

  const exportAnalysis = () => {
    const analysis = currentAnalysis();
    if (!analysis) return;
    saveAs(
      new Blob([JSON.stringify(analysis, null, 2)], { type: 'application/json' }),
      analysisFileName(analysis.sheet)
    );
  };

  const importAnalysis = async (file: File) => {
    try {
      const analysis = parseAnalysis(await file.text());
      if (selectedMusicSheet && analysis.sheet.title !== selectedMusicSheet.title) {
        message.error(translate('analysis.otherPiece', { title: analysis.sheet.title }));
        return;
      }
      applyAnalysis(analysis);
      message.success(translate('analysis.imported', { count: analysis.patterns.length }));
    } catch (error) {
      message.error(translate('analysis.invalid', { reason: (error as Error).message }));
    }
  };

  // Selecting notes in the sheet starts the paper's operator workflow.
  useEffect(() => {
    if (newPattern) setAnalysisTab('operators');
  }, [newPattern]);

  // A new selection accepts the pattern whose suggestions were still open, as "Accept" would: its default operator
  // groups become visible (audit ISSUE-002: it used to be deleted silently).
  const previousSuggestingID = useRef<string | undefined>(undefined);
  useEffect(() => {
    const pendingID = previousSuggestingID.current;
    previousSuggestingID.current = suggestingID;
    if (!pendingID || pendingID === suggestingID || !suggestingID) return;

    setPatterns((oldPatterns) => {
      const pending = oldPatterns.find((pattern) => pattern.id === pendingID);
      if (!pending) return oldPatterns;
      pending.connectedToByOperator
        .filter((connection) => visibleOperatorsByDefault.includes(connection.byOperator))
        .forEach((connection) => {
          const group = oldPatterns.find((pattern) => pattern.id === connection.id);
          if (group) group.isVisible = true;
        });
      return [...oldPatterns];
    });
  }, [suggestingID]);

  useEffect(() => {
    if (theme.colorByVoice) {
      // Retain the current colors of the patterns to re-use them after coloring by pattern, again.
      setTheme((oldTheme) => ({
        ...oldTheme,
        availablePatternColors: [
          ...oldTheme.availablePatternColors,
          ...patterns.map((pattern) => pattern.color).reverse()
        ]
      }));

      setPatterns((oldPatterns) => {
        oldPatterns.forEach(
          (oldPattern) => (oldPattern.color = theme.getVoiceColor(occursFirstInVoice(oldPattern)))
        );

        return [...oldPatterns];
      });
    } else {
      setPatterns((oldPatterns) => {
        oldPatterns.forEach((oldPattern) => {
          oldPattern.color = oldPattern.id.startsWith('family-')
            ? colors[oldPattern.id.slice('family-'.length)]
            : theme.availablePatternColors.pop();
        });

        return [...oldPatterns];
      });
    }
  }, [theme.colorByVoice]);

  useEffect(() => {
    if (
      absoluteOffsetPageMap.size > 0 &&
      (hoveredPatternIDs.length > 0 || hoveredOccurrences.length > 0) &&
      patterns.length > 0
    ) {
      let firstOffset;
      const hoveredPatterns = patterns.filter((pattern) => hoveredPatternIDs.includes(pattern.id));

      if (hoveredOccurrenceIndex !== undefined && hoveredPatterns.length === 1) {
        // Undefined for one render after entries of the pattern were hidden (Suggestions panel).
        const hoveredOccurrence = hoveredPatterns[0].occurrences[hoveredOccurrenceIndex];

        firstOffset = hoveredOccurrence?.firstOffset;
      } else if (hoveredOccurrences.length > 0) {
        firstOffset = Math.min(...hoveredOccurrences.map((occurrence) => occurrence.firstOffset));
      } else {
        firstOffset = Math.min(
          ...hoveredPatterns.flatMap((hoveredPattern) =>
            hoveredPattern.occurrences.map((occurrence) => occurrence.firstOffset)
          )
        );
      }

      const occurrencePage = absoluteOffsetPageMap.get(firstOffset);
      if (occurrencePage !== undefined && occurrencePage !== currentPage) {
        setCurrentPage(occurrencePage);
      }
    }
  }, [absoluteOffsetPageMap, hoveredOccurrenceIndex, hoveredOccurrences, hoveredPatternIDs]);

  const describeFamily = (family: Family) =>
    translate('discovery.patternDescription', {
      bars: `${family.occurrences[0].measure}–${family.occurrences[family.occurrences.length - 1].endMeasure}`,
      count: family.occurrences.length,
      role: familyName(family, labels, translate, familyNames)
    });

  const renameFamily = (family: Family, name: string) =>
    setFamilyNames((oldNames) => {
      const names = { ...oldNames };
      const trimmed = name.trim();
      // An empty name, or the starting name, goes back to the starting name.
      if (!trimmed || trimmed === automaticFamilyName(family, labels, translate))
        delete names[family.label];
      else names[family.label] = trimmed;
      return names;
    });

  const showFamilies = (shown: Family[], visible: boolean) =>
    setPatterns((oldPatterns) => {
      const ids = shown.map(familyPatternID);
      const others = oldPatterns.filter((pattern) => !ids.includes(pattern.id));
      if (!visible) return others;

      return [
        ...others,
        ...shown.map((family) =>
          familyToPattern(
            family,
            familyPatternID(family),
            theme.colorByVoice
              ? theme.getVoiceColor(family.prototype.voiceNumber)
              : colors[family.label],
            describeFamily(family),
            labels[family.label],
            hiddenFamilyOperators[family.label]
          )
        )
      ];
    });

  // Shown families follow the current discovery: a saved or imported analysis keeps which families were shown, while
  // their notes, labels and colours come from the discovery (they change with its algorithm and the palette). A colour
  // the analyst chose for a family stays; automatic ones (earlier palettes, voices, today's roles) are renewed.
  useEffect(() => {
    if (!discovery.data || !selectedMusicSheet || restoredSheetID !== selectedMusicSheet.id) return;
    if (discovery.data.title !== selectedMusicSheet.title) return;
    const automaticColors = new Set([...Object.values(colors), ...voiceColors]);

    setPatterns((oldPatterns) => {
      let isChanged = false;
      const synced = oldPatterns.flatMap((pattern) => {
        if (!pattern.id.startsWith('family-')) return [pattern];
        const family = families.find((candidate) => familyPatternID(candidate) === pattern.id);
        if (family === undefined) {
          isChanged = true;
          return [];
        }
        const current = familyToPattern(
          family,
          pattern.id,
          theme.colorByVoice
            ? theme.getVoiceColor(family.prototype.voiceNumber)
            : colors[family.label],
          describeFamily(family),
          labels[family.label],
          hiddenFamilyOperators[family.label]
        );
        const isAutomatic = isLegacyColor(pattern.color) || automaticColors.has(pattern.color);
        const updated: Pattern = {
          ...current,
          color: isAutomatic ? current.color : pattern.color,
          // Derived from the shown families by the effect below.
          connectedToByOccurrences: pattern.connectedToByOccurrences,
          isLocked: pattern.isLocked,
          isVisible: pattern.isVisible
        };
        if (isEqual(updated, pattern)) return [pattern];
        isChanged = true;
        return [updated];
      });

      return isChanged ? synced : oldPatterns;
    });
  }, [discovery.data, restoredSheetID, familyNames, hiddenFamilyOperators]);

  // Shown families are connected in the graph by the entries they share (see familyConnections).
  const shownFamilyKey = visibleFamilyLabels.slice().sort().join('|');
  useEffect(() => {
    const connections = familyConnections(
      families,
      patterns.map((pattern) => pattern.id)
    );
    setPatterns((oldPatterns) => {
      let isChanged = false;
      const connected = oldPatterns.map((pattern) => {
        const current = connections.get(pattern.id);
        if (current === undefined || isEqual(current, pattern.connectedToByOccurrences))
          return pattern;
        isChanged = true;
        return { ...pattern, connectedToByOccurrences: current };
      });
      return isChanged ? connected : oldPatterns;
    });
  }, [discovery.data, shownFamilyKey]);

  const selectFamily = (family: Family) => {
    setSelectedFamilyLabel(family.label);
    if (!visibleFamilyLabels.includes(family.label)) showFamilies([family], true);
  };

  // The whole piece plays from the beginning of the page on view (from its very beginning on page 1); playing music
  // turns the pages with it.
  const pageStarts = useMemo(
    () => [...absoluteOffsetPageMap.entries()].sort(([first], [second]) => first - second),
    [absoluteOffsetPageMap]
  );
  const pageStartOffset =
    currentPage <= 1 ? 0 : (pageStarts.find(([, page]) => page === currentPage)?.[0] ?? 0);
  const showPageOf = (offset: number) => {
    let low = 0;
    let high = pageStarts.length - 1;
    let page: number | undefined;
    while (low <= high) {
      const middle = (low + high) >> 1;
      if (pageStarts[middle][0] <= offset + 1e-6) {
        page = pageStarts[middle][1];
        low = middle + 1;
      } else high = middle - 1;
    }
    if (page !== undefined) setCurrentPage(page);
  };
  const latestShowPageOf = useRef(showPageOf);
  latestShowPageOf.current = showPageOf;

  // The written notes of the sheet (drawn anew when it is zoomed); a playing entry lights up its own notes.
  const noteSpans = useMemo(() => noteSpansOf(noteElements), [noteElements]);
  const latestNoteSpans = useRef(noteSpans);
  latestNoteSpans.current = noteSpans;
  const followEntry = (playback: Playback, entry: Occurrence) => {
    latestShowPageOf.current(entry.firstOffset);
    followPlayback(() => latestNoteSpans.current, playback, {
      isFollowed: (span) =>
        span.voice === entry.voiceNumber &&
        span.onset >= entry.firstOffset - 1e-6 &&
        span.onset <= entry.lastOffset + 1e-6,
      offsetAt: (position) => entry.firstOffset + position,
      onOffset: (offset) => latestShowPageOf.current(Math.min(offset, entry.lastOffset))
    });
  };

  const playFamily = (family: Family) => {
    if (playing?.label === family.label) {
      playing.playback.stop();
      setPlaying(undefined);
      return;
    }
    let onset = 0;
    const notes = family.notes.map((name, index) => {
      const note = {
        duration: family.rhythm[index],
        midi: name === 'rest' ? [] : [midiFromName(name)],
        onset
      };
      onset += family.rhythm[index];
      return note;
    });
    const playback = play(notes);
    setPlaying({ label: family.label, playback });
    followEntry(playback, family.prototype);
    playback.finished.then(() =>
      setPlaying((current) => (current?.playback === playback ? undefined : current))
    );
  };

  const playOccurrence = async (occurrence: Occurrence) => {
    let notes: PlayableNote[];
    try {
      notes = (
        await axios.get<PlayableNote[]>(`${getAPIURL()}/notes/${selectedMusicSheetID}`, {
          params: {
            firstOffset: occurrence.firstOffset,
            lastOffset: occurrence.lastOffset,
            voiceNumber: occurrence.voiceNumber
          }
        })
      ).data;
    } catch (error) {
      message.error(translate('audio.failed', { reason: apiErrorMessage(error) }));
      return;
    }
    const playback = play(notes);
    setPlaying({ label: occurrenceKey(occurrence), playback });
    followEntry(playback, occurrence);
    playback.finished.then(() =>
      setPlaying((current) => (current?.playback === playback ? undefined : current))
    );
  };

  const hoverOccurrence = (occurrence: FamilyOccurrence | undefined) =>
    setHoveredOccurrences(occurrence ? [occurrence] : []);

  const voiceNumbers = voices.map((voice) => voice.number);
  const lastOffset = selectedMusicSheet?.lastOffset ?? 0;

  return (
    <FullHeightLayout className="melodyvis-shell">
      <Header
        className="flex items-center gap-4 border-0 border-b border-solid border-slate-200"
        // antd's component styles are unlayered and beat Tailwind's utilities (see index.css), so inline.
        style={{
          background: '#ffffff',
          flexWrap: 'wrap',
          height: 'auto',
          lineHeight: 1.5,
          padding: '8px 20px',
          rowGap: 8
        }}
      >
        <h1 className="m-0 whitespace-nowrap text-lg font-semibold tracking-tight text-slate-900">
          MelodyVis
        </h1>
        {/* On narrow screens the sheet selection takes a row of its own. */}
        <div className="min-w-0 flex-[1_1_520px]">
          <MusicSheetSelection
            currentPage={currentPage}
            isMusicSheetRendered={isMusicSheetRendered}
            musicSheets={musicSheets}
            numberOfPages={numberOfPages}
            selectedMusicSheetID={selectedMusicSheetID}
            setConfiguringID={setConfiguringID}
            setCurrentPage={setCurrentPage}
            setIsMusicSheetRendered={setIsMusicSheetRendered}
            setNewPattern={setNewPattern}
            setNumberOfPages={setNumberOfPages}
            setPatterns={setPatterns}
            setSelectedMusicSheetID={setSelectedMusicSheetID}
            setShowOnBoarding={setShowOnBoarding}
            setSuggestingID={setSuggestionsID}
          />
        </div>
        {selectedMusicSheetID && <PageModeSwitch onChange={setPageMode} pageMode={pageMode} />}
        {selectedMusicSheetID && <SheetZoom onChange={setSheetZoom} zoom={sheetZoom} />}
        {selectedMusicSheetID && (
          <ScorePlayer
            key={selectedMusicSheetID}
            musicSheetID={selectedMusicSheetID}
            noteElements={noteElements}
            onPosition={showPageOf}
            startOffset={pageStartOffset}
          />
        )}
        <Dropdown
          menu={{
            items: [
              { icon: <SaveOutlined />, key: 'export', label: translate('analysis.export') },
              { icon: <FolderOpenOutlined />, key: 'import', label: translate('analysis.import') }
            ],
            onClick: ({ key }) =>
              key === 'export' ? exportAnalysis() : analysisFileInput.current?.click()
          }}
        >
          <Button>
            {translate('analysis.menu')} <DownOutlined />
          </Button>
        </Dropdown>
        <input
          accept=".json,application/json"
          aria-label={translate('analysis.import')}
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) importAnalysis(file);
            event.target.value = '';
          }}
          ref={analysisFileInput}
          type="file"
        />
        <Tooltip title={translate('import.title')}>
          <Button icon={<ImportOutlined />} onClick={() => setIsImportOpen(true)}>
            {translate('import.button')}
          </Button>
        </Tooltip>
        <AlignedSegmented
          aria-label={translate('language')}
          onChange={(language) => i18n.changeLanguage(language as string)}
          options={[
            {
              icon: <img alt="Deutsch" src={germanFlag} title="Deutsch" width={16} />,
              value: 'de'
            },
            {
              icon: <img alt="English" src={englishFlag} title="English" width={16} />,
              value: 'en'
            }
          ]}
          size="small"
          value={i18n.languages[0]}
        />
      </Header>
      <Content className="min-h-0 bg-white px-5 pb-3 pt-3">
        <OnBoarding
          isVisible={showOnBoarding}
          setIsVisible={setShowOnBoarding}
          showTab={setAnalysisTab}
        />
        <ImportSheet
          isOpen={isImportOpen}
          onClose={() => setIsImportOpen(false)}
          onImported={async (sheet) => {
            await reloadMusicSheets();
            setSelectedMusicSheetID(sheet.id);
          }}
        />
        <div className="melodyvis-workspace" id={mainWrapperID}>
          <div className="melodyvis-sheet-column relative">
            <ErrorBoundary name={translate('views.sheet')} resetKey={selectedMusicSheetID}>
              <MelodyAnalysis
                currentPage={currentPage}
                hoveredOccurrenceIndex={hoveredOccurrenceIndex}
                hoveredOccurrences={hoveredOccurrences}
                hoveredPatternIDs={hoveredPatternIDs}
                isMusicSheetRendered={isMusicSheetRendered}
                musicSheets={musicSheets}
                noteBeamElements={noteBeamElements}
                noteElements={noteElements}
                noteStemElements={noteStemElements}
                numberOfPages={numberOfPages}
                pageMode={pageMode}
                patterns={patterns}
                selectedMusicSheet={selectedMusicSheet}
                setAbsoluteOffsetPageMap={setAbsoluteOffsetPageMap}
                setConfiguringID={setConfiguringID}
                setCurrentPage={setCurrentPage}
                setHoveredOccurrenceIndex={setHoveredOccurrenceIndex}
                setHoveredPatternIDs={setHoveredPatternIDs}
                setIsMusicSheetRendered={setIsMusicSheetRendered}
                setNewPattern={setNewPattern}
                setNoteBeamElements={setNoteBeamElements}
                setNoteElements={setNoteElements}
                setNoteStemElements={setNoteStemElements}
                setNumberOfPages={setNumberOfPages}
                setPatterns={setPatterns}
                setSelectedMusicSheetID={setSelectedMusicSheetID}
                setSuggestingID={setSuggestionsID}
                setZoom={setSheetZoom}
                suggestingID={suggestingID}
                voices={voices}
                zoom={sheetZoom}
              />
            </ErrorBoundary>
            {musicSheetError && (
              // Over the sheet, which stays mounted: the next selection renders as usual (audit F09).
              <div className="absolute inset-0 z-10 flex items-start justify-center bg-white pt-16">
                <Result
                  extra={
                    <Button onClick={retryMusicSheet} type="primary">
                      {translate('musicSheetError.retry')}
                    </Button>
                  }
                  status="warning"
                  subTitle={musicSheetError}
                  title={translate('musicSheetError.title')}
                />
              </div>
            )}
          </div>
          <div className="melodyvis-analysis-column">
            <ErrorBoundary name={translate('views.timeline')} resetKey={selectedMusicSheetID}>
              <Timeline
                absoluteOffsetPageMap={absoluteOffsetPageMap}
                hoveredOccurrenceIndex={hoveredOccurrenceIndex}
                hoveredOccurrences={hoveredOccurrences}
                hoveredPatternIDs={hoveredPatternIDs}
                patterns={patterns}
                selectedMusicSheet={selectedMusicSheet}
                setConfiguringID={setConfiguringID}
                setCurrentPage={setCurrentPage}
                setHoveredOccurrenceIndex={setHoveredOccurrenceIndex}
                setHoveredPatternIDs={setHoveredPatternIDs}
                onPlayOccurrence={playOccurrence}
                playingOccurrenceKey={playing?.label}
                voices={voices}
              />
            </ErrorBoundary>
            <Tabs
              activeKey={analysisTab}
              className="min-h-0 flex-1"
              items={[
                {
                  children: (
                    <ErrorBoundary
                      name={translate('views.discovery')}
                      resetKey={selectedMusicSheetID}
                    >
                      <div className="flex min-h-0 flex-col gap-4" id={discoveryPanelID}>
                        <DiscoveryPanel
                          colors={colors}
                          discovery={discovery.data}
                          error={discovery.error}
                          isLoading={discovery.isLoading}
                          labels={labels}
                          names={familyNames}
                          onPlay={playFamily}
                          onRename={renameFamily}
                          onSelect={selectFamily}
                          onToggle={(family) =>
                            showFamilies([family], !visibleFamilyLabels.includes(family.label))
                          }
                          onToggleAll={(visible) => showFamilies(families, visible)}
                          ownPatterns={ownPatterns}
                          playingLabel={playing?.label}
                          selectedLabel={selectedFamilyLabel}
                          visibleLabels={visibleFamilyLabels}
                        />
                        {selectedFamily && (
                          <FamilyDetail
                            family={{ ...selectedFamily, label: labels[selectedFamily.label] }}
                            lastOffset={lastOffset}
                            onHoverOccurrence={hoverOccurrence}
                            onPlayOccurrence={playOccurrence}
                            onSelectOccurrence={(occurrence) => {
                              const page = absoluteOffsetPageMap.get(occurrence.firstOffset);
                              if (page !== undefined) setCurrentPage(page);
                            }}
                            voices={voiceNumbers}
                          />
                        )}
                      </div>
                    </ErrorBoundary>
                  ),
                  key: 'discovery',
                  label: translate('discovery.title')
                },
                {
                  children: (
                    <ErrorBoundary
                      name={translate('views.operators')}
                      resetKey={selectedMusicSheetID}
                    >
                      {/* The graph takes the height of the row: without a minimum it shrank to the Suggestions hint. */}
                      <div
                        className="grid h-full min-h-[36rem] grid-cols-[fit-content(190px)_minmax(0,1fr)] gap-x-2"
                        style={{ height: '-webkit-fill-available' }}
                      >
                        <Suggestions
                          configuringID={configuringID}
                          family={
                            suggestionsFamily && {
                              color:
                                patterns.find(
                                  (pattern) => pattern.id === familyPatternID(suggestionsFamily)
                                )?.color ?? colors[suggestionsFamily.label],
                              family: suggestionsFamily,
                              hiddenOperators: hiddenFamilyOperators[suggestionsFamily.label] ?? [],
                              isShown: visibleFamilyLabels.includes(suggestionsFamily.label),
                              label: labels[suggestionsFamily.label],
                              name: familyName(suggestionsFamily, labels, translate, familyNames),
                              onHideOperators: (hidden) =>
                                setHiddenFamilyOperators((oldHidden) => ({
                                  ...oldHidden,
                                  [suggestionsFamily.label]: hidden
                                })),
                              onHoverOccurrences: setHoveredOccurrences,
                              onShow: () => showFamilies([suggestionsFamily], true)
                            }
                          }
                          hoveredPatternIDs={hoveredPatternIDs}
                          musicSheetID={selectedMusicSheetID}
                          noteElements={noteElements}
                          newPattern={newPattern}
                          patterns={patterns}
                          setConfiguringID={setConfiguringID}
                          setHoveredPatternIDs={setHoveredPatternIDs}
                          setNewPattern={setNewPattern}
                          setPatterns={setPatterns}
                          setSuggestingID={setSuggestionsID}
                          setVisibleOperatorsByDefault={setVisibleOperatorsByDefault}
                          suggestingID={suggestingID}
                          visibleOperatorsByDefault={visibleOperatorsByDefault}
                        />
                        <MelodyGraph
                          configuringID={configuringID}
                          hoveredOccurrences={hoveredOccurrences}
                          hoveredPatternIDs={hoveredPatternIDs}
                          patterns={patterns}
                          setConfiguringID={setConfiguringID}
                          setHoveredOccurrenceIndex={setHoveredOccurrenceIndex}
                          setHoveredOccurrences={setHoveredOccurrences}
                          setHoveredPatternIDs={setHoveredPatternIDs}
                          setPatterns={setPatterns}
                          setSuggestingID={setSuggestionsID}
                        />
                      </div>
                    </ErrorBoundary>
                  ),
                  // Kept mounted: the operator search and the graph layout keep their state across tab switches.
                  forceRender: true,
                  key: 'operators',
                  label: translate('tabs.operators')
                },
                {
                  children: (
                    <ErrorBoundary
                      name={translate('views.transitions')}
                      resetKey={selectedMusicSheetID}
                    >
                      {ownPattern ? (
                        ownFamily.data ? (
                          <FamilyDetail
                            family={{ ...ownFamily.data, label: ownPattern.label }}
                            lastOffset={lastOffset}
                            onHoverOccurrence={hoverOccurrence}
                            onPlayOccurrence={playOccurrence}
                            onSelectOccurrence={(occurrence) => {
                              const page = absoluteOffsetPageMap.get(occurrence.firstOffset);
                              if (page !== undefined) setCurrentPage(page);
                            }}
                            voices={voiceNumbers}
                          />
                        ) : (
                          <p className="text-sm text-slate-500">
                            {ownFamily.error ?? translate('transitions.loading')}
                          </p>
                        )
                      ) : configuringFamily ? (
                        <FamilyDetail
                          family={{ ...configuringFamily, label: labels[configuringFamily.label] }}
                          lastOffset={lastOffset}
                          onHoverOccurrence={hoverOccurrence}
                          onPlayOccurrence={playOccurrence}
                          onSelectOccurrence={(occurrence) => {
                            const page = absoluteOffsetPageMap.get(occurrence.firstOffset);
                            if (page !== undefined) setCurrentPage(page);
                          }}
                          voices={voiceNumbers}
                        />
                      ) : (
                        <p className="text-sm text-slate-500">{translate('transitions.hint')}</p>
                      )}
                    </ErrorBoundary>
                  ),
                  key: 'transitions',
                  label: translate('tabs.transitions')
                }
              ]}
              onChange={(key) => setAnalysisTab(key as AnalysisTab)}
              size="small"
            />
          </div>
        </div>
      </Content>
    </FullHeightLayout>
  );
};

export default App;
